import {
  CustomApiResponse,
  PaginatedApiResponse,
} from "utils/helper/ApiResponses";
import { GetInfoQuery, InfoData, PredifineItem } from "utils/types/Info.types";
import { apiSrcUrl } from "../ApiRoutes";
import { ApiService } from "../apiservices";

/**
 * Creates the authorization headers for a private request.
 */
function createAuthHeaders(): { [key: string]: string } {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

// --- Public Info API ---

/**
 * Fetches public data from the /public/info endpoints.
 * Handles single items, lists, and paginated lists.
 */
export async function getPublicInfo<T>(
  slug: string[],
  query?: Partial<GetInfoQuery>
): Promise<CustomApiResponse<T | PaginatedApiResponse<T>>> {
  const queryString = query ? new URLSearchParams(query as any).toString() : "";
  const path = `${apiSrcUrl}/public/info/${slug.join("/")}${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await ApiService.get<
    CustomApiResponse<T | PaginatedApiResponse<T>>
  >(path);
  return data;
}

// --- Private (Admin) Info API ---

/**
 * (Admin) Adds new info to a specified tab.
 * Note: For 'predifine', the payload should be { subCategory: string, payload: object }
 */
export async function postInfo(
  slug: string[],
  payload: any
): Promise<CustomApiResponse<InfoData | PredifineItem>> {
  const path = `${apiSrcUrl}/private/info/${slug.join("/")}`;
  const { data } = await ApiService.post<
    CustomApiResponse<InfoData | PredifineItem>
  >(path, payload, createAuthHeaders());
  return data;
}

/**
 * (Admin) Updates existing info in a specified tab.
 * Note: For 'predifine', the payload should include { subCategory: string, id: string }
 */
export async function putInfo(
  slug: string[],
  payload: any
): Promise<CustomApiResponse<InfoData | PredifineItem>> {
  const path = `${apiSrcUrl}/private/info/${slug.join("/")}`;
  const { data } = await ApiService.put<
    CustomApiResponse<InfoData | PredifineItem>
  >(path, payload, createAuthHeaders());
  return data;
}

/**
 * (Admin) Deletes existing info from a specified tab.
 * This function now matches your backend controller logic.
 * The `id` (and `subCategory` for predifine) is sent in the request body.
 *
 * e.g., deleteInfo(['steps'], 'item-id-123')
 * e.g., deleteInfo(['providers'], 'Netflix')
 * e.g., deleteInfo(['predifine', 'provider'], 'item-id-456')
 */
export async function deleteInfo(
  slug: string[],
  id: string
): Promise<CustomApiResponse<any>> {
  
  const path = `${apiSrcUrl}/private/info/${slug.join('/')}`;
  
  // The slug array already contains the tab and optional subCategory.
  // The backend controller will extract 'tab' and 'subCategory' from the slug.
  // It only needs the 'id' from the body.
  const payload = { id };

  const { data } = await ApiService.delete<CustomApiResponse<any>>(
    path, 
    payload, 
    createAuthHeaders()
  );
  return data;
}